import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  ChevronRight,
  Clock,
  Download,
  RefreshCcw,
  Search,
  XCircle,
} from "lucide-react";
import { useAdminTransfers } from "../../hooks/useAdmin";
import { useReconciliationSweeps } from "../../hooks/useTransferOperations";
import { usePermissions, PERMISSIONS } from "../../hooks/usePermissions";
import { useFilterParams } from "../../hooks/useFilterParams";
import { adminService } from "../../services/admin.service";
import { Pagination } from "./Pagination";

const PAGE_SIZE = 25;

// `processing` and `unsuccessful` are server-side buckets over several stored
// statuses, so these chips agree with the Overview cards that link to them.
const STATUS_FILTERS = [
  { label: "All", value: "all" },
  { label: "Processing", value: "processing" },
  { label: "Completed", value: "completed" },
  { label: "Unsuccessful", value: "unsuccessful" },
  { label: "Manual review", value: "manual_review" },
  { label: "Compliance review", value: "compliance_review" },
  { label: "Refunding", value: "refunding" },
];

const FILTER_DEFAULTS = {
  search: "",
  status: "all",
  offset: 0,
};

export function AdminTransfers() {
  const navigate = useNavigate();
  // Filters in the URL: shareable with a colleague, survives a refresh, and
  // lets the Overview cards link straight to the queue they describe.
  const { values, setValues, setFilters } = useFilterParams(FILTER_DEFAULTS);
  const { search, status, offset } = values;

  const [searchInput, setSearchInput] = useState(search);
  const [isExporting, setIsExporting] = useState(false);

  const { can } = usePermissions();
  const canExecute = can(PERMISSIONS.TRANSFERS_EXECUTE);
  const { reconcileBridge, reconcileZuba, isSweeping } = useReconciliationSweeps();

  useEffect(() => {
    setSearchInput((current) => (current === search ? current : search));
  }, [search]);

  useEffect(() => {
    const typed = searchInput.trim();
    if (typed === search) return undefined;
    const timer = setTimeout(
      () => setValues({ search: typed, offset: 0 }, { replace: true }),
      300
    );
    return () => clearTimeout(timer);
  }, [searchInput, search, setValues]);

  const params = useMemo(
    () => ({ limit: PAGE_SIZE, offset, status, search }),
    [offset, status, search]
  );

  const { transfers, total, isLoading, error, refetch, isFetching } =
    useAdminTransfers(params);

  /**
   * Export the whole filtered result set, not just the page on screen -
   * exporting only the visible rows silently produces an incomplete report.
   */
  async function exportCsv() {
    setIsExporting(true);
    try {
      const pageSize = 100;
      let collected = [];
      let cursor = 0;
      // Bounded so a huge result set cannot spin forever.
      while (cursor < (total || 0) && collected.length < 5000) {
        const page = await adminService.listTransfers({
          limit: pageSize,
          offset: cursor,
          status,
          search,
        });
        collected = collected.concat(page.items ?? []);
        if (!page.items?.length) break;
        cursor += pageSize;
      }
      downloadCsv(collected);
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="space-y-8 text-left">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A2540]/40 mb-1">
            Financial Operations
          </h2>
          <h1 className="text-2xl md:text-3xl text-[#0A4730] font-serif font-semibold">
            Transfers Management
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-4 py-3 rounded-2xl border border-slate-200 bg-white text-[#0A4730] font-semibold text-xs disabled:opacity-50 hover:bg-slate-50 transition"
          >
            <RefreshCcw size={15} className={isFetching ? "animate-spin" : ""} /> Refresh
          </button>
          {canExecute && (
            <>
              <button
                onClick={() => reconcileBridge()}
                disabled={isSweeping}
                className="inline-flex items-center gap-2 px-4 py-3 rounded-2xl border border-slate-200 bg-white text-[#0A4730] font-semibold text-xs disabled:opacity-50 hover:bg-slate-50 transition"
                title="Poll Bridge for every onramp still processing"
              >
                Reconcile Bridge
              </button>
              <button
                onClick={() => reconcileZuba()}
                disabled={isSweeping}
                className="inline-flex items-center gap-2 px-4 py-3 rounded-2xl border border-slate-200 bg-white text-[#0A4730] font-semibold text-xs disabled:opacity-50 hover:bg-slate-50 transition"
                title="Poll Zuba for every onramp still processing"
              >
                Reconcile Zuba
              </button>
            </>
          )}
          <button
            onClick={exportCsv}
            disabled={isExporting || !total}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-[#0A4730] text-white font-semibold text-xs disabled:opacity-50 hover:bg-[#0A4730]/90 transition"
          >
            <Download size={16} /> {isExporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-sm text-red-600">
          Could not load transfers from the admin API.
        </div>
      )}

      <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-6">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:w-96">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              size={18}
            />
            <input
              aria-label="Search transfers"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search reference, sender, or recipient…"
              className="w-full rounded-xl border border-slate-200 pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5"
            />
          </div>
          <div className="flex items-center gap-1 bg-[#FDFDFD] p-1 rounded-2xl border border-slate-200 w-full md:w-auto overflow-x-auto">
            {STATUS_FILTERS.map((option) => (
              <button
                key={option.value}
                onClick={() => setFilters({ status: option.value })}
                className={`px-4 py-2 rounded-xl text-xs font-semibold capitalize whitespace-nowrap ${
                  status === option.value
                    ? "bg-[#0A4730] text-white shadow-sm"
                    : "text-[#0A2540]/70 hover:bg-[#F5F9F4]"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-100">
                <Header>Transfer ID</Header>
                <Header>Sender &amp; Recipient</Header>
                <Header>Amounts</Header>
                <Header>Status</Header>
                <Header>Timestamp</Header>
                <Header align="right">Actions</Header>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <EmptyRow text="Loading transfers…" />
              ) : transfers.length === 0 ? (
                <EmptyRow text="No transfers match your criteria." />
              ) : (
                transfers.map((transfer) => (
                  <tr
                    key={transfer.id}
                    className="hover:bg-[#F5F9F4]/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/admin/transfers/${transfer.id}`)}
                  >
                    <td className="py-4 pr-4 font-mono text-xs font-semibold text-[#0A4730]">
                      {transfer.public_reference}
                    </td>
                    <td className="py-4 pr-4">
                      <div className="font-medium text-[#0A2540]">
                        {transfer.sender_name || transfer.sender_email || "Unknown sender"}
                      </div>
                      <div className="text-xs text-slate-400">
                        To: {transfer.recipient_name || "Unknown recipient"}
                      </div>
                    </td>
                    <td className="py-4 pr-4">
                      <div className="font-semibold text-[#0A4730]">
                        {money(transfer.send_amount, transfer.send_currency)}
                      </div>
                      <div className="text-xs text-[#0A4730]/70">
                        {transfer.receive_amount
                          ? money(transfer.receive_amount, transfer.receive_currency)
                          : `${transfer.receive_currency} pending`}
                      </div>
                    </td>
                    <td className="py-4 pr-4">
                      <TransferStatusBadge status={transfer.status} />
                    </td>
                    <td className="py-4 pr-4 text-xs text-slate-500">
                      {formatDate(transfer.created_at)}
                    </td>
                    <td className="py-4 text-right">
                      <span className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-[#C0F6B7]/30 text-[#0A4730] font-semibold text-xs">
                        Inspect <ChevronRight size={14} />
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          total={total}
          limit={PAGE_SIZE}
          offset={offset}
          onChange={(next) => setValues({ offset: next })}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}

function downloadCsv(rows) {
  const header = [
    "reference",
    "sender",
    "recipient",
    "send_amount",
    "send_currency",
    "receive_amount",
    "receive_currency",
    "status",
    "created_at",
  ];
  const body = rows.map((transfer) => [
    transfer.public_reference,
    transfer.sender_name || transfer.sender_email || transfer.sender_user_id,
    transfer.recipient_name || transfer.recipient_id,
    transfer.send_amount,
    transfer.send_currency,
    transfer.receive_amount || "",
    transfer.receive_currency,
    transfer.status,
    transfer.created_at,
  ]);
  const csv = [header, ...body].map((row) => row.map(csvCell).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `blupay-transfers-${new Date().toISOString().slice(0, 10)}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function Header({ children, align = "left" }) {
  return (
    <th
      className={`pb-4 text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A2540]/40 ${
        align === "right" ? "text-right" : ""
      }`}
    >
      {children}
    </th>
  );
}

function EmptyRow({ text }) {
  return (
    <tr>
      <td colSpan="6" className="py-10 text-center text-sm text-[#0A2540]/50">
        {text}
      </td>
    </tr>
  );
}

function TransferStatusBadge({ status }) {
  const config =
    status === "completed"
      ? { style: "bg-emerald-50 text-emerald-600 border-emerald-100", icon: CheckCircle2 }
      : ["failed", "cancelled", "refunded"].includes(status)
      ? { style: "bg-red-50 text-red-500 border-red-100", icon: XCircle }
      : { style: "bg-amber-50 text-amber-600 border-amber-100", icon: Clock };
  const Icon = config.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border capitalize ${config.style}`}
    >
      <Icon size={14} />
      {humanize(status)}
    </span>
  );
}

function csvCell(value) {
  return `"${String(value ?? "").replaceAll('"', '""')}"`;
}

function humanize(value = "") {
  return value.replaceAll("_", " ");
}

function money(value, currency) {
  return `${currency || ""} ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`.trim();
}

function formatDate(value) {
  return value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";
}
